import { APIAction } from '@app/constants'
import {
  accessDenied, apiError,
  networkError, notAuthorized, clearApiState
} from '@app/actions/api'

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  SERVER_ERROR_PATTERN: /^5\d+/
}

const CONTENT_TYPE = {
  JSON_PATTERN: /application\/json/
}

const RETRY_CONFIG = {
  DELAY: 500,
  METHODS: ['get']
}

const isValidResponse = (response) => {
  const contentType = response.headers['content-type']
  return CONTENT_TYPE.JSON_PATTERN.test(contentType) || !contentType
}

const handleSuccessfulResponse = (response, onSuccess, dispatch) => {
  if (!isValidResponse(response)) {
    window.location.reload()
    return
  }
  
  if (onSuccess) {
    dispatch(onSuccess(response))
  }
  dispatch(clearApiState())
}

const dispatchErrorByStatus = (error, dispatch) => {
  const { response } = error
  
  if (!response) return
  
  const { status } = response
  
  if (status === HTTP_STATUS.FORBIDDEN) {
    dispatch(accessDenied(error))
  } else if (status === HTTP_STATUS.UNAUTHORIZED) {
    dispatch(notAuthorized(error))
  } else if (HTTP_STATUS.SERVER_ERROR_PATTERN.test(status)) {
    dispatch(apiError(error))
  }
}

const shouldRetryRequest = (error) => {
  const method = (error.config?.method || '').toLowerCase()
  return (!error.response || !error.response.status) && 
         RETRY_CONFIG.METHODS.includes(method)
}

const createRetryPromise = (requestor, onSuccess, onFailure, dispatch) => {
  return new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.DELAY))
    .then(() => requestor())
    .then(response => handleSuccessfulResponse(response, onSuccess, dispatch))
    .catch(retryError => {
      dispatch(networkError(retryError))
      
      if (onFailure) {
        dispatch(onFailure(retryError))
      }
      
      dispatchErrorByStatus(retryError, dispatch)
    })
}

const handleApiError = (error, requestor, onSuccess, onFailure, dispatch) => {
  if (shouldRetryRequest(error)) {
    return createRetryPromise(requestor, onSuccess, onFailure, dispatch)
  }

  if (!error.response || !error.response.status) {
    dispatch(networkError(error))
    return
  }

  if (onFailure) {
    dispatch(onFailure(error))
  }

  dispatchErrorByStatus(error, dispatch)
}

const apiMiddleware = ({ dispatch }) => next => action => {
  next(action)

  if (action.type !== APIAction) return;

  const {
    requestor,
    onBeforeSend,
    onSuccess,
    onFailure
  } = action.payload;

  if (onBeforeSend) { dispatch(onBeforeSend()) }

  return requestor()
    .then(response => handleSuccessfulResponse(response, onSuccess, dispatch))
    .catch(error => handleApiError(error, requestor, onSuccess, onFailure, dispatch))
}

export default apiMiddleware
